import { adminNavigationMenu, adminNavigationSelection } from "../../../../../../shared/frontend/admin/adminNavigationCatalog";
import { memberAdminCatalog } from "./adminNavigationCatalog";
import { areaAccessContextFromAuth, communicationArea, financeArea, isAreaEntryVisible, salesArea, staffArea, type AreaAccessContext, type AreaEntry } from "../navigation/areaCatalog";
import { rowAccessPermissions } from "../permissions/permissionRows";
import { filterSettingsItems, settingsCatalogGroups, settingsGroupCapabilityEnabled } from "../../pages/settings/settingsCatalog";
import { isToolsItemVisible, toolsCatalogGroups } from "../../pages/tools/toolsCatalog";
import type { AppShellIconKey } from "../../../../../../shared/frontend/layout/appShellIcons";
import type { AppShellMenuItem } from "../../../../../../shared/frontend/layout/appShellMenu";



import type { AuthInfo } from "../api/types";
import {
  reportCalendarPermissions,
  reportContractsPermissions,
  reportFinancesPermissions,
  reportLeadsPermissions,
  reportListPermissions,
  reportMembersPermissions,
  reportOverviewPermissions,
  reportUsagePermissions
} from "../constants/reportPermissions";
import type { LocalizedText } from "../i18n/i18n";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";

export type MenuRoute = {
  path: string;
  name: string;
  icon?: AppShellIconKey;
  children?: MenuRoute[];
  hideInMenu?: boolean;
  openInNewTab?: boolean;
};

function areaChildren(entries: AreaEntry[], ctx: AreaAccessContext, text: (value: LocalizedText) => string): MenuRoute[] {
  return entries.filter((entry) => isAreaEntryVisible(entry, ctx)).map((entry) => ({ path: entry.path, name: text(entry.titleKey), icon: entry.menuIcon }));
}

const settingsMenuIcons: Record<string, AppShellIconKey> = {
  organisation: "TeamOutlined",
  members: "IdcardOutlined",
  products: "ShoppingOutlined",
  payments: "CreditCardOutlined",
  contractSigning: "FileTextOutlined",
  leads: "BranchesOutlined",
  memberCare: "HeartOutlined",
  communication: "MailOutlined",
  calendar: "CalendarOutlined",
  staff: "ScheduleOutlined",
  access: "KeyOutlined",
  system: "DesktopOutlined"
};

const settingsMenuTitles: Record<string, LocalizedText> = {
  organisation: "Menu.organisation",
  payments: "Menu.payments",
  calendar: "Menu.appointments",
  system: "System"
};

/**
 * Menü aus den Bereichskatalogen (`app/navigation/areaCatalog`, `settingsCatalog`, `toolsCatalog`).
 * Ein Bereich erscheint, sobald einer seiner Einträge erlaubt ist; Rechte stammen aus den
 * Rechtezeilen des Rolleneditors, Capability-Vorgaben aus dem Backend-Katalog.
 */
export function buildMenu(auth: AuthInfo | null, text: (value: LocalizedText) => string): MenuRoute[] {
  const isPlatformAdmin = (auth?.role || "") === PLATFORM_ADMIN_ROLE;
  const ctx = areaAccessContextFromAuth(auth);
  const hasAnyPerm = ctx.hasAnyPermission;
  const hasCalendar = ctx.capabilityEnabled("calendar");
  const hasLeadManager = ctx.capabilityEnabled("leadManager");
  const hasMemberCare = ctx.capabilityEnabled("memberCare");
  const hasReports = ctx.capabilityEnabled("reports");

  // Ein Produktkatalog versorgt gemeinsame Admin-Gruppen, Übersichten und Zurückwege.
  if (isPlatformAdmin) {
    return adminNavigationMenu(memberAdminCatalog, text).map(item => ({ path: item.path ?? item.key, name: item.label, icon: item.icon, children: item.children?.map(child => ({ path: child.key, name: child.label, icon: child.icon })) }));
  }

  const reportChildren: MenuRoute[] = [
    ...(hasAnyPerm(...reportListPermissions) ? [{ path: "/reports/lists", name: text("Custom lists"), icon: "FileTextOutlined" as const }] : []),
    ...(hasAnyPerm(...reportMembersPermissions) ? [{ path: "/reports/group/members", name: text("Members"), icon: "TeamOutlined" as const }] : []),
    ...(hasAnyPerm(...reportContractsPermissions) ? [{ path: "/reports/group/contracts", name: text("Contracts"), icon: "FileDoneOutlined" as const }] : []),
    ...(hasAnyPerm(...reportFinancesPermissions) ? [{ path: "/reports/group/finances", name: text("Finances"), icon: "DollarOutlined" as const }] : []),
    ...(hasAnyPerm(...reportUsagePermissions) ? [{ path: "/reports/group/usage", name: text("Usage"), icon: "LineChartOutlined" as const }] : []),
    ...(hasCalendar && hasAnyPerm(...reportCalendarPermissions) ? [{ path: "/reports/group/calendar", name: text("Calendar"), icon: "CalendarOutlined" as const }] : []),
    ...(hasLeadManager && hasAnyPerm(...reportLeadsPermissions) ? [{ path: "/reports/group/leads", name: text("Leads"), icon: "BranchesOutlined" as const }] : [])
  ];

  const toolsChildren: MenuRoute[] = toolsCatalogGroups
    .flatMap((g) => g.items)
    .filter((item) => isToolsItemVisible(item, ctx))
    .map((item) => ({ path: item.path, name: text(item.titleKey ?? item.title), icon: item.menuIcon }));

  const settingsVisibility = {
    capabilities: auth?.capabilities,
    role: auth?.role || "",
    hasAnyPermission: hasAnyPerm,
    // Kanalabhängige Seiten (SMS/WhatsApp) teilen ihre Rechtezeile mit den Brief-/E-Mail-Texten.
    channels: { email: true, letter: true, sms: true, whatsApp: true }
  };
  const settingsChildren: MenuRoute[] = settingsCatalogGroups
    .filter((g) => settingsGroupCapabilityEnabled(g.key, auth) && filterSettingsItems(g.items, settingsVisibility).length > 0)
    .map((g) => ({ path: `/settings/group/${g.key}`, name: text(settingsMenuTitles[g.key] ?? g.titleKey), icon: settingsMenuIcons[g.key] ?? "SettingOutlined" }));

  const group = (path: string, name: LocalizedText, icon: AppShellIconKey, children: MenuRoute[]): MenuRoute[] =>
    children.length > 0 ? [{ path, name: text(name), icon, children }] : [];

  const routes: MenuRoute[] = [
    ...(hasAnyPerm(...rowAccessPermissions("dashboard")) ? [{ path: "/overview", name: text("Navigation.overview"), icon: "DashboardOutlined" as const }] : []),
    ...(hasAnyPerm(...rowAccessPermissions("checkin")) ? [{ path: "/checkin", name: text("Check-in"), icon: "LoginOutlined" as const }] : []),
    ...(hasAnyPerm(...rowAccessPermissions("tasks")) ? [{ path: "/tasks", name: text("Tasks"), icon: "CheckCircleOutlined" as const }] : []),
    ...(hasAnyPerm(...rowAccessPermissions("persons"))
      ? [
          {
            path: "/persons",
            name: text("People"),
            // Keine Untereinträge: die Personenliste filtert selbst nach Mitgliedern, Interessenten und
            // Ehemaligen; /persons/members usw. bleiben als Einstiege mit vorgewähltem Filter.
            icon: "TeamOutlined" as const
          }
        ]
      : []),
    ...(hasMemberCare && hasAnyPerm(...rowAccessPermissions("memberCare"))
      ? [{ path: "/member-care", name: text("Member care"), icon: "HeartOutlined" as const }]
      : []),
    ...(hasCalendar && hasAnyPerm(...rowAccessPermissions("appointments"))
      ? [{ path: "/calendar", name: text("Calendar"), icon: "CalendarOutlined" as const }]
      : []),
    ...group("/sales", "Sales", "ShopOutlined", areaChildren(salesArea, ctx, text)),
    ...group("/communication", "Communication", "MailOutlined", areaChildren(communicationArea, ctx, text)),
    ...group("/finance", "Finance", "DollarOutlined", areaChildren(financeArea, ctx, text)),
    ...(hasReports && hasAnyPerm(...reportOverviewPermissions) ? group("/reports", "Reports", "BarChartOutlined", reportChildren) : []),
    ...group("/staff", "Staff", "ScheduleOutlined", areaChildren(staffArea, ctx, text)),
    ...group("/tools", "Tools", "ToolOutlined", toolsChildren),
    ...group("/settings", "Settings", "SettingOutlined", settingsChildren)
  ];

  return routes.filter((r) => !r.hideInMenu && (!r.children || r.children.length > 0));
}

/** Pfad für Menü-Matching (ohne trailing slash). */
export function normalizeMenuPath(p: string): string {
  const s = (p || "").trim();
  if (!s) return "";
  if (s.length > 1 && s.endsWith("/")) return s.slice(0, -1);
  return s;
}

export function findMenuRouteByPath(nodes: MenuRoute[], key: string): MenuRoute | null {
  for (const n of nodes) {
    const p = normalizeMenuPath(n.path);
    if (p === key) return n;
    if (n.children?.length) {
      const f = findMenuRouteByPath(n.children, key);
      if (f) return f;
    }
  }
  return null;
}

function ancestorKeysForPath(nodes: MenuRoute[], targetKey: string, stack: string[]): string[] | null {
  for (const n of nodes) {
    const p = normalizeMenuPath(n.path);
    if (!p) continue;
    if (p === targetKey) return stack;
    if (n.children?.length) {
      const inner = ancestorKeysForPath(n.children, targetKey, [...stack, p]);
      if (inner) return inner;
    }
  }
  return null;
}

/**
 * Auswahl und aufzuklappende Submenüs für Ant Design Menu (längster Menü-Pfad, der zur URL passt).
 */
export function computeMenuSelection(effectivePathname: string, routes: MenuRoute[]): { selectedKeys: string[]; openKeys: string[] } {
  const normPath = normalizeMenuPath(routes.some(route => route.path === memberAdminCatalog.homePath)
    ? adminNavigationSelection(memberAdminCatalog, effectivePathname) : effectivePathname);
  if (!normPath) return { selectedKeys: [], openKeys: [] };

  let best: { key: string; len: number } | null = null;

  function walk(nodes: MenuRoute[]) {
    for (const n of nodes) {
      const p = normalizeMenuPath(n.path);
      if (!p) continue;
      if (normPath === p || normPath.startsWith(p + "/")) {
        if (!best || p.length > best.len) best = { key: p, len: p.length };
      }
      if (n.children?.length) walk(n.children);
    }
  }
  walk(routes);

  const bestMatch = best as { key: string; len: number } | null;
  if (!bestMatch) return { selectedKeys: [], openKeys: [] };

  const selectedKeys = [bestMatch.key];
  let openKeys = ancestorKeysForPath(routes, bestMatch.key, []) ?? [];
  const node = findMenuRouteByPath(routes, bestMatch.key);
  /** Auch auf dem Hub (z. B. /settings) die Gruppe aufklappen — nicht nur unter /settings/…. */
  if (node?.children?.length && !openKeys.includes(bestMatch.key)) {
    openKeys = [...openKeys, bestMatch.key];
  }

  return { selectedKeys, openKeys };
}

export type MenuRoutesToItemsOptions = {
  /** Nur Hauptebene: Klick auf den Titel (vor dem Ein-/Ausklappen): z. B. zum Hub navigieren, wenn die Gruppe geöffnet wird. */
  onTopLevelSubmenuTitleClick?: (path: string) => void;
  depth?: number;
};

export function menuRoutesToAntdItems(routes: MenuRoute[], options?: MenuRoutesToItemsOptions): AppShellMenuItem[] {
  const depth = options?.depth ?? 0;
  const out: AppShellMenuItem[] = [];
  for (const r of routes) {
    const key = normalizeMenuPath(r.path);
    if (!key) continue;
    if (r.children?.length) {
      const childOpts: MenuRoutesToItemsOptions = {
        onTopLevelSubmenuTitleClick: options?.onTopLevelSubmenuTitleClick,
        depth: depth + 1
      };
      out.push({
        key,
        icon: r.icon,
        label: r.name,
        children: menuRoutesToAntdItems(r.children, childOpts) ?? [],
        ...(depth === 0 && options?.onTopLevelSubmenuTitleClick
          ? { onTitleClick: () => options.onTopLevelSubmenuTitleClick!(key) }
          : {})
      });
    } else {
      out.push({ key, icon: r.icon, label: r.name });
    }
  }
  return out;
}
